import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { LogOut, ArrowRight } from "lucide-react";
import { StatsCards } from "./StatsCards";
import { TopUsersCard } from "./TopUsersCard";
import { WhitelistTab } from "./WhitelistTab";
import { LoginAttemptsTab } from "./LoginAttemptsTab";
import { ServiceCategoriesTab } from "./ServiceCategoriesTab";
import { AccessRequestsTab } from "./AccessRequestsTab";
import { LandingAnalyticsTab } from "./LandingAnalyticsTab";

const API_URL = process.env.REACT_APP_API_URL || "";
const ADMIN_AUTH_KEY = "unikort_admin_authenticated";

export function AdminDashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>(null);
  const [tab, setTab] = useState<"accessRequests" | "whitelist" | "loginAttempts" | "categories" | "analytics">("accessRequests");
  const [logoutError, setLogoutError] = useState("");

  const refreshStats = useCallback(async () => {
    try {
      const res = await axios.get<{ success: boolean; data: any }>(
        `${API_URL}/api/admin/stats`,
        { withCredentials: true }
      );
      if (res.data.success) setStats(res.data.data);
    } catch {
      /* non-blocking */
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/auth/me`, { withCredentials: true });
        if (!res.data?.isAdmin) {
          sessionStorage.removeItem(ADMIN_AUTH_KEY);
          navigate("/admin", { replace: true });
          return;
        }
        sessionStorage.setItem(ADMIN_AUTH_KEY, "true");
        refreshStats();
      } catch {
        sessionStorage.removeItem(ADMIN_AUTH_KEY);
        navigate("/admin", { replace: true });
      }
    })();
  }, [navigate, refreshStats]);

  const handleLogout = async () => {
    // Only clear the local marker and leave once the server confirms the session
    // is gone — silently navigating away on failure looks like a logout while the
    // privileged cookie stays valid.
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
    } catch {
      setLogoutError(t("admin.logoutFailed", "Не удалось выйти. Проверьте соединение и попробуйте снова."));
      return;
    }
    sessionStorage.removeItem(ADMIN_AUTH_KEY);
    navigate("/admin", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">{t("admin.dashboard")}</h1>
        <Button variant="outline" size="sm" onClick={handleLogout}>
          <LogOut className="w-4 h-4 mr-2" />
          {t("admin.exit")}
        </Button>
      </div>

      {logoutError && (
        <div className="mb-6 rounded-xl border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
          {logoutError}
        </div>
      )}

      <div className="mb-6">
        <StatsCards stats={stats} />
      </div>

      {stats?.topUsers && stats.topUsers.length > 0 && (
        <div className="mb-6">
          <TopUsersCard topUsers={stats.topUsers} />
        </div>
      )}

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList>
          <TabsTrigger value="accessRequests">
            {t("admin.tabs.accessRequests")}
            {stats?.accessRequests?.pending > 0 && ` (${stats.accessRequests.pending})`}
          </TabsTrigger>
          <TabsTrigger value="whitelist">{t("admin.tabs.whitelist")}</TabsTrigger>
          <TabsTrigger value="loginAttempts">{t("admin.tabs.loginAttempts")}</TabsTrigger>
          <TabsTrigger value="categories">{t("admin.tabs.categories")}</TabsTrigger>
          <TabsTrigger value="analytics">{t("admin.tabs.analytics")}</TabsTrigger>
        </TabsList>

        <TabsContent value="accessRequests" className="mt-4">
          <AccessRequestsTab onChange={refreshStats} />
        </TabsContent>

        <TabsContent value="whitelist" className="mt-4">
          <WhitelistTab onChange={refreshStats} />
        </TabsContent>

        <TabsContent value="loginAttempts" className="mt-4">
          <LoginAttemptsTab />
        </TabsContent>

        <TabsContent value="categories" className="mt-4">
          <ServiceCategoriesTab onChange={refreshStats} />
        </TabsContent>

        <TabsContent value="analytics" className="mt-4">
          <LandingAnalyticsTab />
        </TabsContent>
      </Tabs>

      <div className="mt-8 pt-6 border-t border-border">
        <Link
          to="/admin/impersonate"
          className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
        >
          {t("admin.impersonate")}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
